/**
 * One way to render a time, used by every screen.
 *
 * Norican spans DE/FR/ES/DK/CZ/UK/US/IN/CN, so a bare wall-clock time is
 * ambiguous and a bare UTC instant is unreadable. Every timestamp in HROps is
 * therefore shown in the zone the record carries (`local_tz`), with that zone
 * named next to it. An unknown or missing zone falls back to UTC and says UTC,
 * rather than silently using whatever zone the viewer's laptop is set to.
 */

export const UTC = "UTC";

/** The zone this browser is set to — the default when HR picks a time. */
export const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || UTC;

/** Short label for prose: "Europe/Prague" → "Prague", "UTC" → "UTC". */
export function zoneLabel(zone: string): string {
  const city = zone.split("/").pop() ?? zone;
  return city.replace(/_/g, " ");
}

function format(iso: string, zone: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-GB", { ...options, timeZone: zone }).format(new Date(iso));
}

/**
 * An instant rendered in the record's own zone, with the zone kept separate so
 * a caller can put it on a second line in a table or inline in a sentence.
 */
export function inZone(iso: string, tz: string | null | undefined): { time: string; zone: string } {
  const zone = tz || UTC;
  const options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" };
  try {
    return { time: format(iso, zone, options), zone };
  } catch {
    // Intl throws on an unknown IANA zone; show UTC and say so.
    return { time: format(iso, UTC, options), zone: UTC };
  }
}

/** The same instant as one sentence fragment: "16 Sep 2026, 17:00 Prague". */
export function inZoneSentence(iso: string, tz: string | null | undefined): string {
  const { time, zone } = inZone(iso, tz);
  return `${time} ${zoneLabel(zone)}`;
}

/** Date only, no time, in the given zone. Used for flag windows. */
export function dateInZone(iso: string, tz: string | null | undefined = UTC): string {
  const zone = tz || UTC;
  try {
    return format(iso, zone, { dateStyle: "medium" });
  } catch {
    return format(iso, UTC, { dateStyle: "medium" });
  }
}

/** `yyyy-mm-dd` for a date input, n days from today, in the browser's zone. */
export function dateInputValue(daysFromNow: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString().slice(0, 10);
}

/** Whole days between now and `iso`; zero or negative once the window closed. */
export function daysRemaining(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

/**
 * The message to show for a failed call.
 *
 * Every SDK call throws the `ApiError` shape built in `lib/api/client.ts`
 * (`{ status, body }`), where FastAPI's payload is `{ detail }` — a string for a
 * raised `HTTPException`, a list of `{ msg }` for a validation error.
 */
export function errorMessage(error: unknown, fallback = "The request failed."): string {
  const detail = (error as { body?: { detail?: unknown } } | undefined)?.body?.detail;
  if (typeof detail === "string") {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail
      .map((item) => (item as { msg?: string }).msg)
      .filter((msg): msg is string => Boolean(msg));
    if (messages.length > 0) {
      return messages.join("; ");
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}
